import * as React from "react";
import moment from 'moment';
import { AzureMap, AzureMapDataSourceProvider, AzureMapLayerProvider, AzureMapPopup, AzureMapsProvider, IAzureMapOptions, IAzureMapPopupEvent } from "react-azure-maps";
import { AuthenticationType, data } from 'azure-maps-control'
import { useEffect, useRef, useState } from "react";
import { PointList } from "./PointList";
import { wrapper } from "../../auth/styles/activityIndicatorContainer.styles";
import { IContainerPoint } from "../interfaces/IContainerPoint";
import { ContainerPopupHtml } from "./ContainerPopupHtml";
import { IReceptionPlacePoint } from "../interfaces/IReceptionPlacePoint";
import { RootStateOrAny, useDispatch, useSelector } from "react-redux";
import { PointTypeEnum } from "../enums/PointTypeEnum";
import { ReceptionPlacesPopupHtml } from "./ReceptionPlacePopupHtml";
import { IInteralStorePoint } from "../interfaces/IInternalStorePoint";
import { InteralStorePopupHtml } from "./InternalStorePopupHtml";
import { setContainer, setInternalStore, setReceptionPlace } from "../../../config/redux/storeMap/StoreMapActions";
import { setContainers, toggleContainersModal } from "../../../config/redux/container/containersActions";
import { ContainerSearchBox } from "./ContainerSearchBox";
import { IReceptionPlacePointLocalization } from "../interfaces/IReceptionPlacePointLocalization";
import { ReceptionPlaceBoundary } from "./ReceptionPlaceBoundary";
import { MapGlyphFixer, transformMapRequest } from "./MapGlyphFixer";
import { IInternalStoreBorderPoint } from "../interfaces/IInternalStoreBorderPoint";
import { InternalStoreBoundary } from "./InternalStoreBoundary";

const { REACT_APP_AZURE_MAP_KEY } = process.env;

const AzureMapDataSourceProviderAny: React.ComponentType<any> = AzureMapDataSourceProvider;

const option: IAzureMapOptions = {
    authOptions: {
        authType: AuthenticationType.subscriptionKey,
        subscriptionKey: REACT_APP_AZURE_MAP_KEY
    },
    center: [19.4803, 52.0693],
    zoom: 5.7,
    //view: 'Auto',
    transformRequest: transformMapRequest,
}

export interface StoreMapMainProps {
    receptionPlacePoints: IReceptionPlacePoint[];
    receptionPlacePointLocalizations: IReceptionPlacePointLocalization[];
    containerPoints: IContainerPoint[];
    allContainerPoints: IContainerPoint[];
    internalStorePoints: IInteralStorePoint[];
    internalStoreBorderPoints: IInternalStoreBorderPoint[];
    mapType: string
}

export const StoreMapMain: React.FC<StoreMapMainProps> = ({ receptionPlacePoints, receptionPlacePointLocalizations, containerPoints, allContainerPoints, internalStorePoints, internalStoreBorderPoints, mapType }) => {
    const dispatch = useDispatch();
    const currentContainerRef = useRef<IContainerPoint | undefined>(undefined);
    const containerPointsRef = useRef<IContainerPoint[]>([]);
    useEffect(() => { containerPointsRef.current = containerPoints; }, [containerPoints]);
    const [popupVisible, setPopupVisible] = useState<boolean>(false);
    const [popupPosition, setPopupPosition] = useState<data.Position>(new data.Position(
        0,
        0));
    const [html, setHtml] = useState<any>(<></>);
    const [pointType, setPointType] = useState<Number>(0);

    const { showReceptionPlacePoints, showContainerPoints, showInternalStorePoints, selectedReceptionPlace, selectedInternalStore, highlightSilentContainers } = useSelector((state: RootStateOrAny) => state.storeMap);

    const SILENT_THRESHOLD_MS = 48 * 60 * 60 * 1000;
    const silentContainerPoints = React.useMemo(() => {
        if (!highlightSilentContainers) return [];
        return containerPoints.filter(function(c) {
            if (!c.lastActiveDate) return false;
            var m = moment(c.lastActiveDate as string, 'DD-MM-YYYY HH:mm:ss', true);
            if (!m.isValid()) return false;
            return (Date.now() - m.valueOf()) > SILENT_THRESHOLD_MS;
        });
    }, [containerPoints, highlightSilentContainers]);

    const createDataPosition = (longitude: number, latitude: number) => {
        return new data.Position(
            longitude,
            latitude)
    }

    useEffect(() => {
        (window as any).__openContainerHistory = () => {
            const c = currentContainerRef.current;
            if (!c) return;
            dispatch(setContainers([{ ...c, id: String(c.id) }]));
            dispatch(toggleContainersModal({ isOpen: true, isMap: true, isFotos: false }));
        };
        (window as any).__copyText = (btn: HTMLElement) => {
            var text = btn.getAttribute('data-v') || '';
            var flash = function() {
                var prev = btn.innerHTML;
                btn.innerHTML = '&#10003;';
                (btn.style as any).color = '#107c10';
                setTimeout(function() { btn.innerHTML = prev; (btn.style as any).color = ''; }, 1500);
            };
            var execFallback = function() {
                var ta = document.createElement('textarea');
                ta.value = text;
                ta.style.position = 'fixed';
                ta.style.opacity = '0';
                document.body.appendChild(ta);
                ta.focus();
                ta.select();
                try { document.execCommand('copy'); flash(); } catch(e) {}
                document.body.removeChild(ta);
            };
            if (navigator.clipboard) {
                navigator.clipboard.writeText(text).then(flash).catch(execFallback);
            } else {
                execFallback();
            }
        };
        return () => {
            delete (window as any).__openContainerHistory;
            delete (window as any).__copyText;
        };
    }, [dispatch]);

    useEffect(() => {
        if (showReceptionPlacePoints == false && pointType == PointTypeEnum.RECEPTION_PLACE)
            setPopupVisible(false);
        if (showContainerPoints == false && pointType == PointTypeEnum.CONTAINER)
            setPopupVisible(false);
        if (showInternalStorePoints == false && pointType == PointTypeEnum.INTERNAL_STORE)
            setPopupVisible(false);
    }, [showReceptionPlacePoints, showContainerPoints, showInternalStorePoints])

    function handleClusterClick(e: any) {
        if (!e.shapes || e.shapes.length === 0) return;
        var mapRef = (window as any).__azureMapRef;
        if (!mapRef) return;
        try {
            var shape = e.shapes[0];
            var pos = shape.getCoordinates ? shape.getCoordinates() : shape.geometry.coordinates;
            var zoom = (mapRef.getCamera().zoom || 10) + 2;
            mapRef.setCamera({ center: pos, zoom: zoom, type: 'ease', duration: 500 });
        } catch(_) {}
    }

    function getCoordinates(e: any) {
        if (e.shapes && e.shapes.length > 0) {
            const prop = e.shapes[0];

            if (prop.properties.y == PointTypeEnum.RECEPTION_PLACE) {
                var receptionPlacePoint: IReceptionPlacePoint | undefined = receptionPlacePoints.find(x => x.id == prop.id);
                if (receptionPlacePoint == undefined)
                    return;
                setPointType(PointTypeEnum.RECEPTION_PLACE);
                setPopupVisible(true);
                setPopupPosition(createDataPosition(
                    receptionPlacePoint.longitude,
                    receptionPlacePoint.latitude)
                );
                setHtml(<ReceptionPlacesPopupHtml receptionPlacePoint={receptionPlacePoint} />)
                dispatch(setContainer(undefined));
                dispatch(setReceptionPlace(receptionPlacePoint))
                dispatch(setInternalStore(undefined));
            }
            else if (prop.properties.y == PointTypeEnum.CONTAINER) {
                var containnerData: IContainerPoint | undefined = containerPointsRef.current.find(x => x.id == prop.id);
                if (containnerData == undefined)
                    return;
                currentContainerRef.current = containnerData;
                setPointType(PointTypeEnum.CONTAINER);
                setPopupVisible(true);
                setHtml(<ContainerPopupHtml containnerData={containnerData} />)
                setPopupPosition(createDataPosition(
                    containnerData.longitude,
                    containnerData.latitude)
                );
                dispatch(setContainer(containnerData));
                dispatch(setReceptionPlace(undefined));
                dispatch(setInternalStore(undefined));
            }
            else if (prop.properties.y == PointTypeEnum.INTERNAL_STORE) {
                var internalStoreData: IInteralStorePoint | undefined = internalStorePoints.find(x => x.id == prop.id);
                if (internalStoreData == undefined)
                    return;
                setPointType(PointTypeEnum.INTERNAL_STORE);
                setPopupVisible(true);
                setHtml(<InteralStorePopupHtml interalStorePopupHtmlProps={internalStoreData} />);
                setPopupPosition(createDataPosition(
                    internalStoreData.longitude,
                    internalStoreData.latitude)
                );
                dispatch(setContainer(undefined));
                dispatch(setReceptionPlace(undefined));
                dispatch(setInternalStore(internalStoreData));
            }
        }
    }

    const memoizedContainerPoints = React.useMemo(
        () => (
            <PointList points={containerPoints} />
        ),
        [containerPoints],
    );

    const memoizedSilentContainerPoints = React.useMemo(
        () => (
            <PointList points={silentContainerPoints} />
        ),
        [silentContainerPoints],
    );

    const memoizedReceptionPlacePoints = React.useMemo(
        () => (
            <PointList points={receptionPlacePoints} />
        ),
        [receptionPlacePoints],
    );

    const memomizedReceptionPlaceLocalizations = React.useMemo(
        () => (
            <PointList points={receptionPlacePointLocalizations} />
        ),
        [receptionPlacePointLocalizations]
    )

    const memomizedReceptionPlaceCircle = React.useMemo(
        () => (
            <ReceptionPlaceBoundary receptionPlace={selectedReceptionPlace} points={receptionPlacePointLocalizations} />
        ),
        [receptionPlacePointLocalizations]
    )

    const memomizedInternalStorePoints = React.useMemo(
        () => (
            <PointList points={internalStorePoints} />
        ),
        [internalStorePoints]
    )

    const memomizedInternalStoreBorderPoints = React.useMemo(
        () => (
            <InternalStoreBoundary points={internalStoreBorderPoints} internalStorePoint={selectedInternalStore} />
        ),
        [internalStoreBorderPoints]
    )

    const styleOptions = React.useMemo(() => ({ style: mapType }), [mapType]);

    const preparePopupOptions = (pointPosition: any) => {
        var options = {
            position: pointPosition,
            pixelOffset: [0, -5]
        }
        return options;
    }

    const handlePopupEvent: IAzureMapPopupEvent = {
        eventName: "close",
        callback: (e) => {
            dispatch(setContainer(undefined));
            dispatch(setInternalStore(undefined));
            dispatch(setReceptionPlace(undefined));
        },
    };

    const memoizedMapPopup = React.useMemo(
        () => (
            <AzureMapPopup
                isVisible={popupVisible}
                options={preparePopupOptions(popupPosition)}
                popupContent={html}
                events={[handlePopupEvent]}
            />
        ),
        [popupVisible, popupPosition, html],
    );

    return (
        <div className={wrapper}>
            <div style={{
                height: '84vh',
                paddingRight: '3vh',
                paddingLeft: '3vh',
                paddingTop: '3vh'
            }}>
                <AzureMapsProvider>
                    <div style={{ position: 'relative', height: '100%' }}>
                    <AzureMap
                        options={option}
                        styleOptions={styleOptions}
                    >
                        <AzureMapDataSourceProvider id={'AzureMapDataSourceProvider_ReceptionPlacePoints'}>
                            <AzureMapLayerProvider
                                options={{
                                    radius: 5,
                                    strokeColor: "#0066CC",
                                    strokeWidth: 6,
                                    color: "#0066CC",
                                    visible: showReceptionPlacePoints
                                }}
                                id={'AzureMapLayerProvider_ReceptionPlacePoints'}
                                type={'BubbleLayer'}
                                events={{
                                    click: getCoordinates,
                                }}
                            />
                            {memoizedReceptionPlacePoints}
                        </AzureMapDataSourceProvider>
                        <AzureMapDataSourceProvider id={'AzureMapDataSourceProvider_ReceptionPlaceCircle'}>
                            <AzureMapLayerProvider
                                options={{
                                    strokeColor: "#0066CC",
                                    strokeWidth: 2,
                                    visible: showReceptionPlacePoints,
                                }}
                                id={'AzureMapLayerProvider_ReceptionPlaceCircle'}
                                type={'LineLayer'}
                            />
                            {memomizedReceptionPlaceCircle}
                        </AzureMapDataSourceProvider>
                        <AzureMapDataSourceProvider id={'AzureMapDataSourceProvider_ReceptionPlaceLocalizations'}>
                            <AzureMapLayerProvider
                                options={{
                                    radius: 1,
                                    strokeColor: "#0066CC",
                                    strokeWidth: 6,
                                    color: "#0066CC",
                                    visible: showReceptionPlacePoints
                                }}
                                id={'AzureMapLayerProvider_ReceptionPlaceLocalizations'}
                                type={'BubbleLayer'}
                            />
                            {memomizedReceptionPlaceLocalizations}
                        </AzureMapDataSourceProvider>
                        <AzureMapDataSourceProviderAny
                            id={'AzureMapDataSourceProvider_ContainerPoints'}
                            dataSourceOptions={{ cluster: true, clusterRadius: 60, clusterMaxZoom: 15 }}
                        >
                            <AzureMapLayerProvider
                                id={'AzureMapLayerProvider_ContainerCluster'}
                                type={'BubbleLayer'}
                                options={{
                                    radius: 18,
                                    color: "#257F09",
                                    strokeWidth: 2,
                                    strokeColor: "#ffffff",
                                    filter: ['has', 'point_count']
                                }}
                                events={{ click: handleClusterClick }}
                            />
                            <AzureMapLayerProvider
                                id={'AzureMapLayerProvider_ContainerClusterCount'}
                                type={'SymbolLayer'}
                                options={{
                                    textOptions: {
                                        textField: ['get', 'point_count_abbreviated'],
                                        color: 'white',
                                        font: ['Arial Unicode MS Bold'],
                                        offset: [0, 0.4]
                                    },
                                    iconOptions: { image: 'none' },
                                    filter: ['has', 'point_count']
                                }}
                            />
                            <AzureMapLayerProvider
                                options={{
                                    radius: 5,
                                    strokeColor: "#257F09",
                                    strokeWidth: 6,
                                    color: "#257F09",
                                    filter: ['!', ['has', 'point_count']]
                                }}
                                id={'AzureMapLayerProvider_ContainerPoints'}
                                type={'BubbleLayer'}
                                events={{
                                    click: getCoordinates,
                                }}
                            />
                            {memoizedContainerPoints}
                        </AzureMapDataSourceProviderAny>
                        <AzureMapDataSourceProvider id={'AzureMapDataSourceProvider_SilentContainerPoints'}>
                            <AzureMapLayerProvider
                                id={'AzureMapLayerProvider_SilentContainerPoints'}
                                type={'BubbleLayer'}
                                options={{
                                    radius: 8,
                                    color: '#cc0000',
                                    strokeColor: '#ffffff',
                                    strokeWidth: 2,
                                }}
                                events={{ click: getCoordinates }}
                            />
                            {memoizedSilentContainerPoints}
                        </AzureMapDataSourceProvider>
                        <AzureMapDataSourceProvider id={'AzureMapDataSourceProvider_InternalStorePoints'}>
                            <AzureMapLayerProvider
                                options={{
                                    radius: 11,
                                    color: "#4f1509",
                                    strokeWidth: 0,
                                    visible: showInternalStorePoints
                                }}
                                id={'AzureMapLayerProvider_InternalStorePoints'}
                                type={'BubbleLayer'}
                                events={{
                                    click: getCoordinates,
                                }}
                            />
                            {memomizedInternalStorePoints}
                        </AzureMapDataSourceProvider>
                        <AzureMapDataSourceProvider id={'AzureMapDataSourceProvider_InternalStoreBorderPoints'}>
                            <AzureMapLayerProvider
                                options={{
                                    strokeColor: "#4f1509",
                                    strokeWidth: 2,
                                    visible: showInternalStorePoints,
                                    //radius: 123
                                }}
                                id={'AzureMapLayerProvider_InternalStoreBorderPoints'}
                                type={'LineLayer'}
                            />
                            {memomizedInternalStoreBorderPoints}
                        </AzureMapDataSourceProvider>
                        {memoizedMapPopup}
                        <MapGlyphFixer />
                    </AzureMap>
                    <ContainerSearchBox
                        containerPoints={allContainerPoints}
                        onSelect={(point) => {
                            currentContainerRef.current = point;
                            setPointType(PointTypeEnum.CONTAINER);
                            setPopupVisible(true);
                            setHtml(<ContainerPopupHtml containnerData={point} />);
                            setPopupPosition(createDataPosition(point.longitude, point.latitude));
                            dispatch(setContainer(point));
                            dispatch(setReceptionPlace(undefined));
                            dispatch(setInternalStore(undefined));
                        }}
                    />
                    </div>
                </AzureMapsProvider>
            </div>
        </div>
    );
}

export default StoreMapMain

//https://azure.github.io/react-azure-maps/index.html
//https://docs.microsoft.com/en-us/azure/azure-maps/how-to-manage-authentication
